/**
 * Gestión de usuarios de un comité (Console superadmin).
 *
 * Lista los usuarios del tenant y permite crear/editar/desactivar/
 * resetear password. La columna `cargo` se valida contra una lista
 * canónica con un escape "Otro" para casos atípicos.
 */
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  KeyRound,
  Pencil,
  Plus,
  ShieldCheck,
  UserMinus,
  UserPlus,
  Users,
} from "lucide-react";

import { useToast } from "../../components/Toast";
import {
  Badge,
  Button,
  Cargando,
  ErrorCarga,
  PageHeader,
  TableWrap,
  type BadgeTone,
} from "../../components/ui";
import { ApiError } from "../../lib/api";
import {
  actualizarUsuarioEmpresa,
  detalleEmpresa,
  listarCargosSugeridos,
  listarUsuariosEmpresa,
  type UsuarioRead,
} from "./api";
import { UsuarioFormModal } from "./UsuarioFormModal";
import { ResetPasswordModal } from "./ResetPasswordModal";
import { AccesosComiteModal } from "./AccesosComiteModal";

export function EmpresaUsuariosPage(): JSX.Element {
  const { empresaId } = useParams<{ empresaId: string }>();
  const id = Number(empresaId);
  const queryClient = useQueryClient();
  const toast = useToast();

  const empresa = useQuery({
    queryKey: ["superadmin", "empresa", id],
    queryFn: () => detalleEmpresa(id),
    enabled: Number.isFinite(id),
  });

  const usuarios = useQuery({
    queryKey: ["superadmin", "empresa", id, "usuarios"],
    queryFn: () => listarUsuariosEmpresa(id),
    enabled: Number.isFinite(id),
  });

  const cargos = useQuery({
    queryKey: ["superadmin", "cargos"],
    queryFn: listarCargosSugeridos,
  });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UsuarioRead | null>(null);
  const [resetting, setResetting] = useState<UsuarioRead | null>(null);
  const [accesos, setAccesos] = useState<UsuarioRead | null>(null);

  const toggleActive = useMutation({
    mutationFn: ({ uid, active }: { uid: number; active: boolean }) =>
      actualizarUsuarioEmpresa(id, uid, { is_active: active }),
    onSuccess: () => {
      toast.success("Estado actualizado");
      void queryClient.invalidateQueries({
        queryKey: ["superadmin", "empresa", id, "usuarios"],
      });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Error al actualizar"),
  });

  const items = useMemo(() => usuarios.data ?? [], [usuarios.data]);
  const activos = items.filter((u) => u.is_active);
  const inactivos = items.filter((u) => !u.is_active);

  if (!Number.isFinite(id)) {
    return <ErrorCarga mensaje="ID de empresa inválido" />;
  }

  return (
    <div className="space-y-5 max-w-5xl">
      <PageHeader
        title="Usuarios del comité"
        icon={Users}
        volver={{
          etiqueta: empresa.data?.empresa.nombre ?? "el comité",
          ruta: `/superadmin/empresas/${id}`,
        }}
        subtitle={
          <>
            {empresa.data?.empresa.nombre ?? "Cargando…"} · {activos.length} activos /{" "}
            {inactivos.length} inactivos
          </>
        }
        actions={
          <Button icon={Plus} onClick={() => setCreating(true)}>
            Nuevo usuario
          </Button>
        }
      />

      <UsuariosTable
        items={items}
        loading={usuarios.isLoading}
        error={usuarios.isError ? usuarios.error : null}
        onReintentar={() => void usuarios.refetch()}
        onEdit={(u) => setEditing(u)}
        onResetPassword={(u) => setResetting(u)}
        onAccesos={(u) => setAccesos(u)}
        onToggleActive={(u) =>
          toggleActive.mutate({ uid: u.id, active: !u.is_active })
        }
        toggleBusy={toggleActive.isPending}
      />

      {creating && (
        <UsuarioFormModal
          mode="create"
          empresaId={id}
          cargosDisponibles={cargos.data ?? []}
          onClose={() => setCreating(false)}
          onSuccess={() => {
            setCreating(false);
            void queryClient.invalidateQueries({
              queryKey: ["superadmin", "empresa", id],
            });
          }}
        />
      )}

      {editing && (
        <UsuarioFormModal
          mode="edit"
          empresaId={id}
          usuario={editing}
          cargosDisponibles={cargos.data ?? []}
          onClose={() => setEditing(null)}
          onSuccess={() => {
            setEditing(null);
            void queryClient.invalidateQueries({
              queryKey: ["superadmin", "empresa", id],
            });
          }}
        />
      )}

      {accesos && (
        <AccesosComiteModal empresaId={id} usuario={accesos} onClose={() => setAccesos(null)} />
      )}

      {resetting && (
        <ResetPasswordModal
          empresaId={id}
          usuario={resetting}
          onClose={() => setResetting(null)}
          onSuccess={() => setResetting(null)}
        />
      )}
    </div>
  );
}

function UsuariosTable({
  items,
  loading,
  error,
  onReintentar,
  onEdit,
  onResetPassword,
  onAccesos,
  onToggleActive,
  toggleBusy,
}: {
  items: UsuarioRead[];
  loading: boolean;
  error: unknown;
  onReintentar: () => void;
  onEdit: (u: UsuarioRead) => void;
  onResetPassword: (u: UsuarioRead) => void;
  onAccesos: (u: UsuarioRead) => void;
  onToggleActive: (u: UsuarioRead) => void;
  toggleBusy: boolean;
}): JSX.Element {
  return (
    <TableWrap label="Usuarios del comité">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-left text-xs uppercase text-neutral-500">
          <tr>
            <th scope="col" className="px-3 py-2">Nombre</th>
            <th scope="col" className="px-3 py-2">Email · username</th>
            <th scope="col" className="px-3 py-2">Rol</th>
            <th scope="col" className="px-3 py-2">Cargo</th>
            <th scope="col" className="px-3 py-2">Estado</th>
            <th scope="col" className="px-3 py-2 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={6} className="px-3">
                <Cargando />
              </td>
            </tr>
          )}
          {error != null && (
            <tr>
              <td colSpan={6} className="px-3 py-4">
                <ErrorCarga error={error} onReintentar={onReintentar} />
              </td>
            </tr>
          )}
          {!loading && items.length === 0 && (
            <tr>
              <td colSpan={6} className="px-3 py-6 text-center text-neutral-500">
                Este comité aún no tiene usuarios.
              </td>
            </tr>
          )}
          {items.map((u) => (
            <tr key={u.id} className="border-t border-neutral-100 hover:bg-neutral-50">
              <td className="px-3 py-2">
                <div className="font-medium text-neutral-900 flex items-center gap-1.5">
                  {u.nombre}
                  {u.is_superadmin && (
                    <span title="Superadmin">
                      <ShieldCheck className="h-3.5 w-3.5 text-amber-600" strokeWidth={1.5} />
                    </span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 text-neutral-600">
                <div>{u.email}</div>
                <div className="text-xs text-neutral-500">@{u.username}</div>
              </td>
              <td className="px-3 py-2">
                <RoleBadge role={u.role} />
              </td>
              <td className="px-3 py-2 text-neutral-700">{u.cargo ?? "—"}</td>
              <td className="px-3 py-2">
                {u.is_active ? (
                  <Badge tone="emerald">Activo</Badge>
                ) : (
                  <Badge tone="slate">Inactivo</Badge>
                )}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {u.is_superadmin ? (
                  <span className="text-xs text-neutral-500">Protegido</span>
                ) : (
                  <div className="inline-flex items-center gap-1">
                    <ActionBtn
                      onClick={() => onEdit(u)}
                      label="Editar"
                      icon={<Pencil className="h-3.5 w-3.5" />}
                    />
                    <ActionBtn
                      onClick={() => onResetPassword(u)}
                      label="Reset password"
                      icon={<KeyRound className="h-3.5 w-3.5" />}
                    />
                    <ActionBtn
                      onClick={() => onAccesos(u)}
                      label="Comités"
                      icon={<Building2 className="h-3.5 w-3.5" />}
                    />
                    <ActionBtn
                      onClick={() => onToggleActive(u)}
                      disabled={toggleBusy}
                      label={u.is_active ? "Desactivar" : "Reactivar"}
                      tone={u.is_active ? "danger" : "success"}
                      icon={
                        u.is_active ? (
                          <UserMinus className="h-3.5 w-3.5" />
                        ) : (
                          <UserPlus className="h-3.5 w-3.5" />
                        )
                      }
                    />
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

function RoleBadge({ role }: { role: UsuarioRead["role"] }): JSX.Element {
  const tone: BadgeTone =
    role === "admin" ? "brand" : role === "funcionario" ? "slate" : "brand";
  return (
    <Badge tone={tone} className="capitalize">
      {role}
    </Badge>
  );
}

function ActionBtn({
  onClick,
  label,
  icon,
  tone = "neutral",
  disabled = false,
}: {
  onClick: () => void;
  label: string;
  icon: React.ReactNode;
  tone?: "neutral" | "danger" | "success";
  disabled?: boolean;
}): JSX.Element {
  const cls =
    tone === "danger"
      ? "bg-rose-50 text-rose-700 hover:bg-rose-100"
      : tone === "success"
        ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
        : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${cls}`}
    >
      {icon}
      {label}
    </button>
  );
}
